import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
    Network,
    Cloud,
    Monitor,
    Users,
    Shield,
    ShieldCheck,
    ShieldAlert,
    Circle,
    Trash2,
    Zap,
    RefreshCw,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
    useRegistry,
    useDeleteRegistry,
    useTestRegistry,
    type RegistryProfile,
} from '@/hooks/use-registries';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useHeaderAction } from '@/lib/header-action-context';

const kindConfig: Record<string, { icon: typeof Cloud; label: string; color: string }> = {
    local: { icon: Monitor, label: 'Local', color: 'text-emerald-600' },
    cloud: { icon: Cloud, label: 'Cloud', color: 'text-blue-600' },
    partner: { icon: Users, label: 'Partner', color: 'text-purple-600' },
};

const statusConfig: Record<string, { dot: string; bg: string; text: string }> = {
    active: { dot: 'text-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700' },
    disabled: { dot: 'text-slate-400', bg: 'bg-slate-50', text: 'text-slate-500' },
    unreachable: { dot: 'text-red-500', bg: 'bg-red-50', text: 'text-red-700' },
};

const trustConfig: Record<string, { icon: typeof Shield; label: string; color: string }> = {
    trusted: { icon: ShieldCheck, label: 'Trusted', color: 'text-emerald-600' },
    verified: { icon: Shield, label: 'Verified', color: 'text-blue-600' },
    untrusted: { icon: ShieldAlert, label: 'Untrusted', color: 'text-amber-600' },
};

function InfoRow({ label, value }: { label: string; value?: string | null }) {
    if (!value) return null;
    return (
        <div className="flex items-start gap-2 py-2">
            <span className="text-sm font-medium text-slate-500 w-40 shrink-0">{label}</span>
            <span className="text-sm text-slate-900 font-mono break-all">{value}</span>
        </div>
    );
}

export default function RegistryDetailPage() {
    const { registryId } = useParams<{ registryId: string }>();
    const navigate = useNavigate();
    const { setHeaderTitle } = useHeaderAction();
    const { data: registry, isLoading } = useRegistry(registryId ?? '');
    const deleteMutation = useDeleteRegistry();
    const testMutation = useTestRegistry(registryId ?? '');
    const [testResult, setTestResult] = useState<{
        reachable: boolean;
        httpOk?: boolean;
        latencyMs: number;
    } | null>(null);

    useEffect(() => {
        if (!registry) return;
        const registryName = registry.display_name ?? registry.alias;
        const kc = kindConfig[registry.kind] ?? kindConfig.cloud;
        const KIcon = kc.icon;
        const sc = statusConfig[registry.status] ?? statusConfig.active;
        setHeaderTitle({
            icon: (
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-slate-100">
                    <KIcon className={`h-4 w-4 ${kc.color}`} />
                </div>
            ),
            title: registryName,
            subtitle: registry.alias,
            badges: (
                <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                    {registry.is_default && <Badge variant="secondary">Default</Badge>}
                    <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${sc.bg} ${sc.text}`}
                    >
                        <Circle size={8} className={`fill-current ${sc.dot}`} />
                        {registry.status}
                    </span>
                </div>
            ),
        });
        return () => setHeaderTitle(null);
    }, [registry, setHeaderTitle]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <RefreshCw size={20} className="animate-spin text-slate-400" />
            </div>
        );
    }

    if (!registry) {
        return (
            <div className="p-6">
                <p className="text-slate-500">Registry not found.</p>
            </div>
        );
    }

    const kc = kindConfig[registry.kind] ?? kindConfig.cloud;
    const tc = trustConfig[registry.trust_tier] ?? trustConfig.untrusted;
    const TrustIcon = tc.icon;

    const handleDelete = async () => {
        await deleteMutation.mutateAsync(registry.id);
        navigate('/registries');
    };

    const handleTest = async () => {
        const result = await testMutation.mutateAsync();
        setTestResult(result);
    };

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <div className="flex justify-end items-center gap-2 mb-8">
                    <button
                        onClick={handleTest}
                        disabled={testMutation.isPending}
                        className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
                    >
                        <Zap size={14} className={testMutation.isPending ? 'animate-pulse' : ''} />
                        {testMutation.isPending ? 'Testing...' : 'Test Connection'}
                    </button>
                    {!registry.is_default && (
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <button className="flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-600 shadow-sm hover:bg-red-50">
                                    <Trash2 size={14} />
                                    Remove
                                </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>Remove Registry</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        This will remove the registry connection and delete all
                                        federated cards from this registry. This action cannot be
                                        undone.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                        onClick={handleDelete}
                                        className="bg-red-600 hover:bg-red-700"
                                    >
                                        Remove Registry
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    )}
            </div>

            {testResult && (
                <div
                    className={`rounded-lg border p-4 mb-6 ${testResult.reachable ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}
                >
                    <div className="flex items-center gap-2">
                        <Circle
                            size={10}
                            className={`fill-current ${testResult.reachable ? 'text-emerald-500' : 'text-red-500'}`}
                        />
                        <span
                            className={`text-sm font-medium ${testResult.reachable ? 'text-emerald-700' : 'text-red-700'}`}
                        >
                            {testResult.reachable ? 'Connection successful' : 'Connection failed'}
                        </span>
                        <span className="text-xs text-slate-500 ml-2">
                            {testResult.latencyMs}ms
                        </span>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="rounded-lg border border-slate-200 bg-white p-5">
                    <h2 className="text-lg font-semibold text-slate-900 mb-4">
                        Connection Details
                    </h2>
                    <div className="divide-y divide-slate-100">
                        <InfoRow label="Alias" value={registry.alias} />
                        <InfoRow label="Kind" value={kc.label} />
                        <InfoRow label="NATS URL" value={registry.nats_url} />
                        <InfoRow label="Registry Subject" value={registry.registry_subject} />
                        <InfoRow label="Base URL" value={registry.base_url} />
                        <InfoRow label="Zone" value={registry.zone} />
                    </div>
                </div>

                <div className="rounded-lg border border-slate-200 bg-white p-5">
                    <h2 className="text-lg font-semibold text-slate-900 mb-4">
                        Trust & Federation
                    </h2>
                    <div className="divide-y divide-slate-100">
                        <div className="flex items-center gap-2 py-2">
                            <span className="text-sm font-medium text-slate-500 w-40 shrink-0">
                                Trust Tier
                            </span>
                            <span className={`inline-flex items-center gap-1.5 text-sm ${tc.color}`}>
                                <TrustIcon size={14} />
                                {tc.label}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 py-2">
                            <span className="text-sm font-medium text-slate-500 w-40 shrink-0">
                                Federation
                            </span>
                            <Badge variant={registry.supports_federation ? 'default' : 'outline'}>
                                {registry.supports_federation ? 'Enabled' : 'Disabled'}
                            </Badge>
                        </div>
                        <div className="flex items-center gap-2 py-2">
                            <span className="text-sm font-medium text-slate-500 w-40 shrink-0">
                                Search
                            </span>
                            <Badge variant={registry.supports_search ? 'default' : 'outline'}>
                                {registry.supports_search ? 'Enabled' : 'Disabled'}
                            </Badge>
                        </div>
                        <InfoRow label="Auth Type" value={registry.auth_type} />
                        {registry.signer_keys.length > 0 && (
                            <div className="py-2">
                                <span className="text-sm font-medium text-slate-500 block mb-1">
                                    Signer Keys
                                </span>
                                <div className="space-y-1">
                                    {registry.signer_keys.map((key, i) => (
                                        <span
                                            key={i}
                                            className="block text-xs font-mono text-slate-600 bg-slate-50 rounded px-2 py-1 break-all"
                                        >
                                            {key}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="rounded-lg border border-slate-200 bg-white p-5 lg:col-span-2">
                    <h2 className="text-lg font-semibold text-slate-900 mb-4">
                        Status & Timestamps
                    </h2>
                    <div className="grid grid-cols-2 gap-x-8 divide-y divide-slate-100">
                        <InfoRow label="Created" value={new Date(registry.created).toLocaleString()} />
                        <InfoRow label="Modified" value={new Date(registry.modified).toLocaleString()} />
                        {registry.last_seen_at && (
                            <InfoRow
                                label="Last Seen"
                                value={new Date(registry.last_seen_at).toLocaleString()}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
